/*
 * Luv, Mud — buddies (<luvmud-buddies>, sections/luvmud-buddies.liquid)
 *
 * A site-wide feature pack that enhances the theme's DOM at runtime (no theme snippet is modified):
 *   fly to cart, cart buddy, product card footprints, scroll mud drip, the M-U-D secret, peekers, the jealous buddy and
 *   the character size picker. Each feature is switched on by an attribute on the element.
 *
 * Sprout (assets/mud-trail-mascot.js) owns the right edge of the screen and celebrates add to cart on its own; the
 * buddies stay on the left / bottom-left / next to the header cart icon and never use the teal character.
 *
 * Every listener is registered with one AbortController and every timer is tracked, so disconnecting the element (the
 * theme editor re-rendering the section) removes everything it added to the page.
 */
(() => {
  if (!window.customElements || window.customElements.get('luvmud-buddies')) return;

  const MUD = ['#6b4426', '#7d5130', '#5a3820', '#8a5d3b'];
  const SIZE_COLORS = ['#f36881', '#5993f1', '#fb7e3b', '#64bfc2'];
  const BUDDIES = ['pink', 'blue', 'orange'];
  const SECRET_KEY = 'luvmud:secret-found';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  const SMALL = window.matchMedia('(max-width: 699px)');

  const rand = (min, max) => min + Math.random() * (max - min);
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reduced = () => REDUCED.matches;

  const el = (tag, className, parent) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (parent) parent.appendChild(node);
    return node;
  };

  const decorative = (node) => {
    node.setAttribute('aria-hidden', 'true');
    return node;
  };

  const inViewport = (rect) =>
    rect.width > 4 && rect.height > 4 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;

  const store = (kind, key, value) => {
    try {
      const area = kind === 'session' ? window.sessionStorage : window.localStorage;
      if (value === undefined) return area.getItem(key);
      area.setItem(key, value);
    } catch (error) {
      /* storage blocked: behave as if empty */
    }
    return null;
  };

  /* --- Sound: tiny Web Audio blips, only after a user gesture and when the shared preference allows ------------------ */

  let gestured = false;
  let audio = null;

  const soundAllowed = () => {
    const activated = navigator.userActivation ? navigator.userActivation.hasBeenActive : gestured;
    if (!activated) return false;
    const pref = store('local', 'luvmud:sound');
    if (pref === 'off') return false;
    if (pref === 'on') return true;
    return !reduced();
  };

  const audioContext = () => {
    if (!audio) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      try {
        audio = new Ctx();
      } catch (error) {
        return null;
      }
    }
    if (audio.state === 'suspended') audio.resume().catch(() => {});
    return audio;
  };

  const play = (kind) => {
    if (!soundAllowed()) return;
    const ctx = audioContext();
    if (!ctx) return;
    const now = ctx.currentTime + 0.01;
    const out = ctx.createGain();
    out.connect(ctx.destination);

    if (kind === 'pop') {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(420, now);
      osc.frequency.exponentialRampToValueAtTime(980, now + 0.09);
      out.gain.setValueAtTime(0.0001, now);
      out.gain.exponentialRampToValueAtTime(0.16, now + 0.015);
      out.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      osc.connect(out);
      osc.start(now);
      osc.stop(now + 0.18);
      return;
    }

    // Splat: a short burst of filtered noise plus a soft low thump
    const length = Math.floor(ctx.sampleRate * 0.3);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 3);
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1400, now);
    filter.frequency.exponentialRampToValueAtTime(300, now + 0.25);
    noise.connect(filter);
    filter.connect(out);
    out.gain.setValueAtTime(0.22, now);
    out.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    noise.start(now);

    const thump = ctx.createOscillator();
    const thumpGain = ctx.createGain();
    thump.frequency.setValueAtTime(140, now);
    thump.frequency.exponentialRampToValueAtTime(55, now + 0.18);
    thumpGain.gain.setValueAtTime(0.2, now);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
    thump.connect(thumpGain);
    thumpGain.connect(ctx.destination);
    thump.start(now);
    thump.stop(now + 0.22);
  };

  /* --- Mud droplets (the hero's droplet look) ----------------------------------------------------------------------- */

  const splash = (parent, x, y, count, spread, options = {}) => {
    if (reduced()) return;
    for (let i = 0; i < count; i++) {
      const drop = el('span', 'lbd-drop', parent);
      const size = rand(options.min || 4, options.max || 9);
      drop.style.cssText = `left:${x - size / 2}px;top:${y - size / 2}px;width:${size}px;height:${size * 1.1}px;--mud:${pick(MUD)}`;
      const angle = options.up ? rand(-Math.PI * 0.95, -Math.PI * 0.05) : rand(0, Math.PI * 2);
      const distance = rand(spread * 0.5, spread);
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      drop
        .animate(
          [
            { transform: 'translate(0, 0) scale(0.4)', opacity: 1 },
            { transform: `translate(${dx}px, ${dy}px) scale(1)`, opacity: 1, offset: 0.55 },
            { transform: `translate(${dx * 1.1}px, ${dy + spread * 0.7}px) scale(0.7)`, opacity: 0 },
          ],
          { duration: rand(520, 760), easing: 'cubic-bezier(.2,.7,.4,1)', fill: 'forwards' }
        )
        .finished.then(() => drop.remove(), () => drop.remove());
    }
  };

  class LuvmudBuddies extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.timers = new Set();
      this.observers = [];
      this.removals = [];
      this.nodes = new Set();
      this.buddySlot = null;

      const has = (name) => this.hasAttribute(name);
      const design = has('design-mode');
      this.pageType = this.getAttribute('page-type') || '';
      this.features = {
        fly: has('fly-to-cart') && !design,
        cart: has('cart-buddy'),
        prints: has('card-footprints'),
        trail: has('scroll-trail'),
        secret: has('secret') && !design,
        peek: has('peekers') && !design && this.pageType !== 'index',
        jealous: has('jealous') && !design,
        sizes: has('size-picker'),
      };

      const sig = this.ac.signal;
      const markGesture = () => (gestured = true);
      document.addEventListener('pointerdown', markGesture, { capture: true, passive: true, signal: sig });
      document.addEventListener('keydown', markGesture, { capture: true, passive: true, signal: sig });

      if (this.features.cart) this.initCartBuddy();
      if (this.features.fly || this.features.cart) this.initCartEvents();
      if (this.features.trail) this.initTrail();
      if (this.features.secret) this.initSecret();
      if (this.features.sizes) this.initSizes();
      if (this.features.prints || this.features.jealous || this.features.peek) this.initPointer();
      if (this.features.peek) this.initPeekers();

      document.addEventListener('visibilitychange', () => this.onVisibility(), { signal: sig });
    }

    disconnectedCallback() {
      this.ac?.abort();
      this.timers?.forEach((id) => window.clearTimeout(id));
      this.timers?.clear();
      if (this.trailFrame) window.cancelAnimationFrame(this.trailFrame);
      this.observers?.forEach((observer) => observer.disconnect());
      this.removals?.forEach((fn) => fn());
      this.nodes?.forEach((node) => {
        node.cleanup?.();
        node.remove();
      });
      this.nodes?.clear();
      this.removals = [];
      this.observers = [];
      this.buddySlot = null;
    }

    /* --- Helpers ------------------------------------------------------------------------------------------------- */

    on(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: this.ac.signal });
    }

    later(fn, ms) {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        fn();
      }, ms);
      this.timers.add(id);
      return id;
    }

    cancel(id) {
      if (!id) return 0;
      window.clearTimeout(id);
      this.timers.delete(id);
      return 0;
    }

    /** Adds a node to the page and makes sure it goes away with the section */
    adopt(node) {
      if (this.nodes.size > 40) this.nodes.forEach((item) => !item.isConnected && this.nodes.delete(item));
      this.nodes.add(node);
      return node;
    }

    mini(character, extraClass) {
      const template = this.querySelector(`template[data-mini="${character}"]`);
      const mini = template?.content.firstElementChild?.cloneNode(true) || decorative(el('span', 'lm-mini'));
      if (extraClass) mini.classList.add(...extraClass.split(' '));
      return mini;
    }

    mood(mini, name, duration) {
      if (mini && typeof mini.setMood === 'function') mini.setMood(name, duration);
    }

    overlayOpen() {
      return !!document.querySelector('cart-drawer[open], x-drawer[open], x-modal[open], dialog[open], quick-buy-modal[open]');
    }

    drawerOpen() {
      return !!document.querySelector('cart-drawer[open]');
    }

    onVisibility() {
      if (document.hidden) {
        if (this.peekTimer) {
          this.peekRemaining = Math.max(1500, this.peekDue - performance.now());
          this.peekTimer = this.cancel(this.peekTimer);
        }
        this.jealousTimer = this.cancel(this.jealousTimer);
        this.hoverTarget = null;
      } else if (this.features.peek && !this.peekTimer && this.peekRemaining) {
        this.schedulePeek(this.peekRemaining);
        this.peekRemaining = 0;
      }
    }

    /* --- 2. Cart buddy ------------------------------------------------------------------------------------------- */

    initCartBuddy() {
      const holder = document.querySelector('.header__cart-link');
      if (!holder) return;

      const wrap = this.adopt(decorative(el('span', 'lbd-cartbud')));
      const hop = el('span', 'lbd-cartbud__hop', wrap);
      const mini = this.mini('pink', 'lbd-cartbud__mini');
      hop.appendChild(mini);
      const zz = el('span', 'lbd-cartbud__z', hop);
      zz.textContent = 'z';
      holder.appendChild(wrap);

      this.cartBuddy = { wrap, hop, mini };
      const fit = () => {
        wrap.classList.remove('is-tight');
        if (wrap.getBoundingClientRect().right + 8 > document.documentElement.clientWidth) wrap.classList.add('is-tight');
      };
      fit();
      this.on(window, 'resize', fit, { passive: true });
      this.count = null;
      this.setCount(parseInt(this.getAttribute('cart-count'), 10) || (document.querySelector('cart-dot.is-visible') ? 1 : 0));

      // Correct the server-rendered count once the page is idle (back/forward cache, other tabs)
      const idle = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1200));
      idle(() => {
        if (this.ac.signal.aborted) return;
        const root = window.Shopify?.routes?.root || '/';
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: this.ac.signal })
          .then((response) => (response.ok ? response.json() : null))
          .then((cart) => {
            if (cart && typeof cart.item_count === 'number' && cart.item_count !== this.count) this.setCount(cart.item_count, true);
          })
          .catch(() => {});
      });

      // Touch devices: tapping the buddy 5 times quickly is the secret (it never opens the cart)
      if (this.features.secret && !FINE.matches) {
        wrap.classList.add('is-tappable');
        let taps = [];
        this.on(wrap, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const now = performance.now();
          taps = taps.filter((time) => now - time < 2500);
          taps.push(now);
          this.cartHop();
          this.mood(mini, 'giggle', 500);
          if (taps.length >= 5) {
            taps = [];
            this.triggerSecret(false);
          }
        });
      }
    }

    setCount(count, animate) {
      const buddy = this.cartBuddy;
      if (!buddy) return;
      const previous = this.count;
      this.count = count;
      if (previous !== null && count < previous) {
        buddy.wrap.dataset.mood = 'pout';
        this.mood(buddy.mini, 'sad');
        this.poutTimer = this.cancel(this.poutTimer);
        this.poutTimer = this.later(() => {
          this.poutTimer = 0;
          this.applyCartMood();
        }, 2500);
        return;
      }
      if (this.poutTimer) return; // finish pouting first; the timer applies the latest count
      this.applyCartMood();
      if (animate && previous !== null && count > previous && !this.flying) this.cartHop();
    }

    applyCartMood() {
      const buddy = this.cartBuddy;
      if (!buddy) return;
      const count = this.count || 0;
      const mood = count === 0 ? 'sleepy' : count < 3 ? 'happy' : 'joy';
      buddy.wrap.dataset.mood = mood;
      this.mood(buddy.mini, mood === 'joy' ? 'giggle' : null);
    }

    cartHop() {
      const hop = this.cartBuddy?.hop;
      if (!hop) return;
      if (reduced()) {
        hop.classList.remove('is-pulsing');
        void hop.offsetWidth;
        hop.classList.add('is-pulsing');
        this.later(() => hop.classList.remove('is-pulsing'), 1200);
        return;
      }
      hop.animate(
        [
          { transform: 'translateY(0) scale(1, 1)' },
          { transform: 'translateY(1px) scale(1.18, 0.8)', offset: 0.15 },
          { transform: 'translateY(-9px) scale(0.92, 1.1)', offset: 0.45 },
          { transform: 'translateY(0) scale(1.1, 0.9)', offset: 0.75 },
          { transform: 'translateY(0) scale(1, 1)' },
        ],
        { duration: 560, easing: 'ease-out' }
      );
    }

    initCartEvents() {
      this.on(document, 'cart:change', (event) => {
        const cart = event.detail?.cart;
        if (this.cartBuddy && cart && typeof cart.item_count === 'number') this.setCount(cart.item_count, true);
      });

      if (!this.features.fly) return; // the cart buddy reacts to the count going up on its own

      // Remember the product card whose quick add was used (the form may live in a quick buy drawer)
      this.on(
        document,
        'click',
        (event) => {
          const card = event.target.closest?.('product-card');
          if (card) {
            this.lastCard = card;
            this.lastCardAt = performance.now();
          }
        },
        { capture: true, passive: true }
      );
      this.on(document, 'variant:add', (event) => this.flyToCart(event.target));
    }

    /* --- 1. Fly to cart ------------------------------------------------------------------------------------------ */

    findSourceImage(form) {
      const visibleImage = (images) => Array.from(images).find((img) => inViewport(img.getBoundingClientRect()));
      const card = form.closest?.('product-card');
      if (card) return card.querySelector('.product-card__figure img');

      const section = form.closest?.('.shopify-section');
      const gallery = section?.querySelector('product-gallery');
      if (gallery) {
        const cell = gallery.querySelector('scroll-carousel')?.selectedCell;
        const img = cell?.querySelector?.('img');
        if (img && inViewport(img.getBoundingClientRect())) return img;
        return visibleImage(gallery.querySelectorAll('.product-gallery__media:not([hidden]) img')) || img || null;
      }

      if (this.lastCard?.isConnected && performance.now() - this.lastCardAt < 120000) {
        return this.lastCard.querySelector('.product-card__figure img');
      }

      const dialog = form.closest?.('quick-buy-modal, x-modal, x-drawer, .quick-buy-modal');
      if (dialog) return visibleImage(dialog.querySelectorAll('img'));
      return null;
    }

    flyToCart(form) {
      if (!form || !(form instanceof Element)) return;
      if (reduced()) {
        this.later(() => this.cartHop(), 60);
        return;
      }

      const img = this.findSourceImage(form);
      let from = img ? img.getBoundingClientRect() : null;
      if (!from || !inViewport(from)) {
        const button = form.querySelector('[type="submit"], button[name="add"]') || document.querySelector(`[form="${form.id}"][type="submit"]`);
        const rect = button?.getBoundingClientRect();
        from = rect && inViewport(rect) ? rect : { left: window.innerWidth / 2 - 50, top: window.innerHeight / 2 - 50, width: 100, height: 100 };
      }

      const cartLink = document.querySelector('.header__cart-link a') || document.querySelector('.header__cart-link');
      const cartRect = cartLink?.getBoundingClientRect();
      const cartVisible = cartRect && cartRect.width > 0 && cartRect.bottom > 4 && cartRect.top < window.innerHeight;
      const x1 = cartVisible ? cartRect.left + cartRect.width / 2 : window.innerWidth - 40;
      const y1 = cartVisible ? cartRect.top + cartRect.height / 2 : 26;

      const size = clamp(Math.min(from.width, from.height), 56, SMALL.matches ? 120 : 170);
      const x0 = from.left + from.width / 2;
      const y0 = from.top + from.height / 2;

      const layer = this.adopt(decorative(el('div', 'lbd-fly-layer', this)));
      const card = el('div', 'lbd-fly', layer);
      card.style.cssText = `width:${size}px;height:${size}px;left:${x0 - size / 2}px;top:${y0 - size / 2}px`;
      if (img) {
        const clone = el('img', '', card);
        clone.alt = '';
        clone.decoding = 'async';
        clone.src = img.currentSrc || img.src;
      } else {
        el('span', 'lbd-parcel', card);
      }

      // Quadratic arc, sampled into keyframes; the effect easing shapes the overall pace
      const cx = x0 + (x1 - x0) * 0.35;
      const cy = Math.min(y0, y1) - clamp(Math.abs(x1 - x0) * 0.35 + 90, 110, 260);
      const endScale = 20 / size;
      const frames = [];
      const steps = 14;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const u = 1 - t;
        const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
        const y = u * u * y0 + 2 * u * t * cy + t * t * y1;
        const scale = 1 + (endScale - 1) * Math.pow(t, 0.8);
        frames.push({ transform: `translate(${x - x0}px, ${y - y0}px) rotate(${-10 + 34 * t}deg) scale(${scale})`, offset: t });
      }
      frames[0].transform = 'translate(0px, 0px) rotate(0deg) scale(0.92)';

      this.flying = true;
      const flight = card.animate(frames, { duration: 900, easing: 'cubic-bezier(.45,.05,.6,.95)', fill: 'forwards' });

      // A few mud drops fall off the parcel along the way
      [0.3, 0.5, 0.68].forEach((t) => {
        this.later(() => {
          const u = 1 - t;
          const x = u * u * x0 + 2 * u * t * cx + t * t * x1;
          const y = u * u * y0 + 2 * u * t * cy + t * t * y1;
          const drop = el('span', 'lbd-drop', layer);
          const s = rand(5, 8);
          drop.style.cssText = `left:${x}px;top:${y}px;width:${s}px;height:${s * 1.15}px;--mud:${pick(MUD)}`;
          drop
            .animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: `translateY(${rand(60, 110)}px) scaleY(1.3)`, opacity: 0 }], {
              duration: 600,
              easing: 'cubic-bezier(.5,0,1,1)',
              fill: 'forwards',
            })
            .finished.then(() => drop.remove(), () => drop.remove());
        }, 900 * t);
      });

      // The orange catcher pops up beside the cart icon just before the parcel arrives
      let catcher = null;
      const small = SMALL.matches;
      const catcherSize = small ? 28 : 32;
      if (cartVisible) {
        this.later(() => {
          if (this.drawerOpen() || !layer.isConnected) return;
          catcher = el('span', 'lbd-catcher', layer);
          catcher.style.cssText = `width:${catcherSize}px;left:${x1 - catcherSize * 0.55}px;top:${y1 - catcherSize * 0.3}px`;
          const mini = this.mini('orange', 'lbd-catcher__mini');
          catcher.appendChild(mini);
          catcher.miniEl = mini;
          catcher.animate(
            [
              { transform: 'translateY(14px) scale(0.2)', opacity: 0 },
              { transform: 'translateY(-4px) scale(1.08)', opacity: 1, offset: 0.7 },
              { transform: 'translateY(0) scale(1)', opacity: 1 },
            ],
            { duration: 300, easing: 'cubic-bezier(.3,1.4,.5,1)', fill: 'forwards' }
          );
          if (mini.lookAt) mini.lookAt(x0, y0, 600);
        }, 480);
      }

      const land = () => {
        this.flying = false;
        card.remove();
        const drawer = this.drawerOpen();
        splash(layer, x1, y1, small ? 6 : 8, small ? 18 : 24, { up: !drawer, min: 3, max: 7 });
        play('pop');
        this.cartHop();
        if (this.cartBuddy) this.mood(this.cartBuddy.mini, 'giggle', 1100);
        this.later(() => this.applyCartMood(), 1150);

        if (!catcher || drawer) {
          catcher?.remove();
          this.later(() => layer.remove(), 800);
          return;
        }
        // After the flight the layer drops under the drawers (z 999) so an opening drawer covers the catcher
        layer.style.zIndex = '5';
        this.mood(catcher.miniEl, 'giggle');
        catcher.animate(
          [
            { transform: 'scale(1, 1)' },
            { transform: 'translateY(2px) scale(1.28, 0.72)', offset: 0.25 },
            { transform: 'translateY(-3px) scale(0.92, 1.1)', offset: 0.6 },
            { transform: 'scale(1, 1)' },
          ],
          { duration: 420, easing: 'ease-out', composite: 'add' }
        );
        this.later(() => {
          catcher
            .animate([{ transform: 'translateY(0) scale(1)', opacity: 1 }, { transform: 'translateY(16px) scale(0.3)', opacity: 0 }], {
              duration: 280,
              easing: 'cubic-bezier(.5,0,.9,.6)',
              fill: 'forwards',
            })
            .finished.then(() => layer.remove(), () => layer.remove());
        }, 900);
      };
      flight.finished.then(land, () => {
        this.flying = false;
        layer.remove();
      });
    }

    /* --- 3. Footprints on product cards / 6. peeker proximity / 7. jealousy ----------------------------------------- */

    initPointer() {
      this.prints = [];
      this.printState = { card: null, x: 0, y: 0, travelled: 0, side: 1 };
      this.lastPointerAt = 0;

      this.on(
        document,
        'pointermove',
        (event) => {
          const fine = event.pointerType === 'mouse' && FINE.matches;
          if (this.buddySlot?.near) this.checkProximity(event.clientX, event.clientY);
          if (!fine) return;
          if (this.features.prints && !reduced()) this.trackPrints(event);
          if (this.features.jealous) this.trackJealousy(event);
        },
        { passive: true }
      );
      this.on(
        document,
        'pointerdown',
        (event) => {
          if (this.buddySlot?.near) this.checkProximity(event.clientX, event.clientY);
          // A click (e.g. quick add) is not "staring": the 2.5s hover count starts again
          this.hoverTarget = null;
          this.jealousTimer = this.cancel(this.jealousTimer);
        },
        { passive: true }
      );
      this.on(document.documentElement, 'pointerleave', () => {
        this.printState.card = null;
        this.hoverTarget = null;
        this.jealousTimer = this.cancel(this.jealousTimer);
      });
    }

    trackPrints(event) {
      const state = this.printState;
      const figure = event.target.closest?.('product-card .product-card__figure');
      if (!figure) {
        state.card = null;
        return;
      }
      if (figure !== state.card) {
        state.card = figure;
        state.x = event.clientX;
        state.y = event.clientY;
        state.travelled = 0;
        return;
      }
      const dx = event.clientX - state.x;
      const dy = event.clientY - state.y;
      const step = Math.hypot(dx, dy);
      if (step < 40) return;
      state.x = event.clientX;
      state.y = event.clientY;
      state.side = -state.side;
      this.addPrint(figure, event.clientX, event.clientY, Math.atan2(dy, dx), state.side);
    }

    addPrint(figure, x, y, angle, side) {
      let box = figure.querySelector(':scope > .lbd-prints');
      if (!box) {
        box = this.adopt(decorative(el('span', 'lbd-prints', figure)));
      }
      const rect = figure.getBoundingClientRect();
      // Feet sit either side of the path, toes pointing the way the pointer travels
      const ox = Math.cos(angle + Math.PI / 2) * 6 * side;
      const oy = Math.sin(angle + Math.PI / 2) * 6 * side;
      const print = el('span', 'lbd-print', box);
      print.style.cssText = `left:${x - rect.left + ox}px;top:${y - rect.top + oy}px;transform:translate(-50%,-50%) rotate(${(angle * 180) / Math.PI + 90}deg)${side < 0 ? ' scaleX(-1)' : ''}`;
      print.addEventListener('animationend', () => {
        print.remove();
        const index = this.prints.indexOf(print);
        if (index > -1) this.prints.splice(index, 1);
      });
      this.prints.push(print);
      while (this.prints.length > 14) this.prints.shift().remove();
    }

    trackJealousy(event) {
      const target = event.target.closest?.('product-card, product-gallery .product-gallery__media');
      if (target === this.hoverTarget) return;
      this.hoverTarget = target;
      this.jealousTimer = this.cancel(this.jealousTimer);
      if (!target || !this.jealousAllowed()) return;
      this.jealousTimer = this.later(() => {
        this.jealousTimer = 0;
        if (this.hoverTarget === target && target.isConnected && !document.hidden && this.jealousAllowed()) this.showJealous(target);
      }, 2500);
    }

    jealousAllowed() {
      const now = performance.now();
      return (this.jealousCount || 0) < 2 && (!this.lastJealousAt || now - this.lastJealousAt > 120000) && this.buddySlot?.kind !== 'jealous' && !this.overlayOpen();
    }

    /* --- 7. Jealous buddy ---------------------------------------------------------------------------------------- */

    showJealous(target) {
      if (this.buddySlot) this.dismissSlot(true);
      this.jealousCount = (this.jealousCount || 0) + 1;
      this.lastJealousAt = performance.now();

      const rect = target.getBoundingClientRect();
      const size = 78;
      const y = clamp(rect.top + rect.height / 2, 150, window.innerHeight - 130);
      const root = this.adopt(decorative(el('div', 'lbd-jealous', this)));
      root.style.top = `${y - size / 2}px`;
      const body = el('span', 'lbd-jealous__body', root);
      const mini = this.mini(pick(BUDDIES), 'lbd-jealous__mini');
      body.appendChild(mini);
      const bubble = el('span', 'lbd-bubble', root);
      bubble.textContent = this.getAttribute('jealous-message') || 'You like that one more than me? 😤';

      const slot = { kind: 'jealous', root, near: false };
      this.buddySlot = slot;
      const still = reduced();

      requestAnimationFrame(() => {
        this.mood(mini, 'sad');
        if (mini.lookAt) mini.lookAt(rect.left + rect.width / 2, rect.top + rect.height / 2, 5200);
      });
      if (!still) {
        root.animate([{ transform: 'translateX(-130%)' }, { transform: 'translateX(6%)', offset: 0.7 }, { transform: 'translateX(0)' }], {
          duration: 560,
          easing: 'cubic-bezier(.3,1.2,.5,1)',
          fill: 'backwards',
        });
        bubble.animate([{ transform: 'scale(0.3) rotate(-2deg)', opacity: 0 }, { transform: 'scale(1) rotate(-2deg)', opacity: 1 }], {
          duration: 300,
          delay: 380,
          easing: 'cubic-bezier(.3,1.5,.5,1)',
          fill: 'backwards',
        });
      }

      slot.timer = this.later(() => {
        if (this.buddySlot !== slot) return;
        this.mood(mini, 'giggle');
        bubble.textContent = this.getAttribute('jealous-after') || 'Okay fine, it IS cute 💛';
        if (!still) {
          bubble.animate([{ transform: 'scale(0.85) rotate(2deg)' }, { transform: 'scale(1.06) rotate(1deg)', offset: 0.6 }, { transform: 'scale(1) rotate(2deg)' }], {
            duration: 320,
            easing: 'ease-out',
          });
          body.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-8px)', offset: 0.4 }, { transform: 'translateY(0)' }], { duration: 420 });
        }
        bubble.classList.add('is-after');
        slot.timer = this.later(() => this.dismissSlot(false), 2400);
      }, 2500);
    }

    /* --- 6. Peekers ---------------------------------------------------------------------------------------------- */

    initPeekers() {
      this.lastScrollAt = 0;
      this.on(window, 'scroll', () => (this.lastScrollAt = performance.now()), { passive: true });
      if (reduced()) return;
      this.schedulePeek(20000);
    }

    schedulePeek(delay) {
      this.peekTimer = this.cancel(this.peekTimer);
      this.peekDue = performance.now() + delay;
      this.peekTimer = this.later(() => {
        this.peekTimer = 0;
        this.tryPeek();
      }, delay);
    }

    tryPeek() {
      if (document.hidden) {
        this.peekRemaining = 2000;
        return;
      }
      if (reduced()) return;
      const now = performance.now();
      if (this.buddySlot || now - this.lastScrollAt < 2000 || this.overlayOpen()) {
        this.schedulePeek(2500);
        return;
      }
      this.peek();
      this.schedulePeek(rand(45000, 90000));
    }

    peek(forceMode) {
      if (this.buddySlot) this.dismissSlot(true); // max one buddy on screen
      const small = SMALL.matches || !FINE.matches;
      const character = pick(BUDDIES);
      let mode = forceMode || 'corner';
      let gallery = null;
      if (!forceMode && !small) {
        gallery = this.pageType === 'product' ? this.visibleGalleryCell() : null;
        const roll = Math.random();
        mode = gallery && roll < 0.4 ? 'gallery' : roll < 0.7 ? 'side' : 'corner';
      }
      if (mode === 'gallery') gallery = gallery || this.visibleGalleryCell();
      if (mode === 'gallery' && !gallery) mode = 'corner';
      if (small && mode === 'side') mode = 'corner';

      const mini = this.mini(character, 'lbd-peek__mini');
      let root;
      let hidden;
      let shown;
      if (mode === 'gallery') {
        gallery.classList.add('lbd-has-peek');
        root = decorative(el('span', 'lbd-gallery-peek', gallery));
        const removeClass = () => gallery.classList.remove('lbd-has-peek');
        root.cleanup = removeClass;
        root.appendChild(mini);
        hidden = 'translateY(105%)';
        shown = 'translateY(46%)';
      } else {
        root = decorative(el('span', `lbd-peek lbd-peek--${mode}`, this));
        if (small) root.classList.add('lbd-peek--small');
        if (mode === 'corner' && small && document.querySelector('.product-sticky-bar.is-visible, [class*="sticky"][class*="add-to-cart"], [class*="product-quick-add"]')) {
          root.style.bottom = '72px';
        }
        if (mode === 'side') root.style.top = `${Math.round(rand(0.42, 0.7) * window.innerHeight)}px`;
        root.appendChild(mini);
        hidden = mode === 'side' ? 'translateX(-102%) rotate(90deg)' : 'translateY(102%) rotate(-6deg)';
        shown = mode === 'side' ? 'translateX(-28%) rotate(90deg)' : 'translateY(34%) rotate(-6deg)';
      }
      this.adopt(root);

      const slot = { kind: 'peek', root, mini, hidden, near: false, rectAt: 0 };
      this.buddySlot = slot;
      mini.style.transform = hidden;
      mini.animate([{ transform: hidden }, { transform: shown }], { duration: 620, easing: 'cubic-bezier(.3,1.45,.5,1)', fill: 'forwards' });
      slot.timer = this.later(() => {
        if (this.buddySlot !== slot) return;
        slot.near = true;
        mini.blink?.();
        slot.timer = this.later(() => this.dismissSlot(false), 4000);
      }, 640);
    }

    visibleGalleryCell() {
      const gallery = document.querySelector('product-gallery');
      if (!gallery) return null;
      const selected = gallery.querySelector('scroll-carousel')?.selectedCell;
      const cells = selected ? [selected] : Array.from(gallery.querySelectorAll('.product-gallery__media:not([hidden])'));
      return (
        cells.find((cell) => {
          const rect = cell.getBoundingClientRect();
          return rect.width > 160 && rect.bottom < window.innerHeight - 20 && rect.bottom > 220;
        }) || null
      );
    }

    checkProximity(x, y) {
      const slot = this.buddySlot;
      if (!slot || !slot.near || slot.kind !== 'peek') return;
      const now = performance.now();
      if (!slot.rect || now - slot.rectAt > 250) {
        slot.rect = slot.mini.getBoundingClientRect();
        slot.rectAt = now;
      }
      const rect = slot.rect;
      const dx = Math.max(rect.left - x, 0, x - rect.right);
      const dy = Math.max(rect.top - y, 0, y - rect.bottom);
      if (Math.hypot(dx, dy) < 120) {
        slot.near = false;
        this.mood(slot.mini, 'surprised');
        this.cancel(slot.timer);
        slot.timer = this.later(() => this.dismissSlot(false), 220);
      }
    }

    dismissSlot(instant) {
      const slot = this.buddySlot;
      if (!slot) return;
      this.buddySlot = null;
      slot.near = false;
      this.cancel(slot.timer);
      const finish = () => {
        slot.root.cleanup?.();
        slot.root.remove();
      };
      if (instant || reduced()) {
        finish();
        return;
      }
      if (slot.kind === 'peek') {
        slot.mini.getAnimations().forEach((animation) => animation.commitStyles?.());
        slot.mini.getAnimations().forEach((animation) => animation.cancel());
        slot.mini.animate([{ transform: getComputedStyle(slot.mini).transform }, { transform: slot.hidden }], { duration: 260, easing: 'cubic-bezier(.6,0,.9,.5)', fill: 'forwards' }).finished.then(finish, finish);
      } else {
        slot.root.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-135%)' }], { duration: 380, easing: 'cubic-bezier(.6,-0.3,.8,.6)', fill: 'forwards' }).finished.then(finish, finish);
      }
    }

    /* --- 4. Scroll mud trail ------------------------------------------------------------------------------------- */

    initTrail() {
      const trail = this.adopt(decorative(el('div', 'lbd-trail', this)));
      el('span', 'lbd-trail__cap', trail);
      const drip = el('span', 'lbd-trail__drip', trail);
      const tip = el('span', 'lbd-trail__tip', trail);
      this.trail = { trail, drip, tip, height: 0, max: 1, splashed: false };

      const measure = () => {
        this.trail.height = trail.clientHeight;
        this.trail.max = document.documentElement.scrollHeight - window.innerHeight;
        trail.classList.toggle('is-hidden', this.trail.max < 240);
        requestTrail();
      };
      const requestTrail = () => {
        if (!this.trailFrame) this.trailFrame = window.requestAnimationFrame(() => this.drawTrail());
      };
      this.on(window, 'scroll', requestTrail, { passive: true });
      this.on(window, 'resize', measure, { passive: true });
      if ('ResizeObserver' in window) {
        let pending = 0;
        const observer = new ResizeObserver(() => {
          if (!pending) pending = window.requestAnimationFrame(() => ((pending = 0), measure()));
        });
        observer.observe(document.body);
        this.observers.push(observer);
      }
      measure();
    }

    drawTrail() {
      this.trailFrame = 0;
      const t = this.trail;
      const progress = clamp(window.scrollY / Math.max(1, t.max), 0, 1);
      t.drip.style.transform = `scaleY(${progress.toFixed(4)})`;
      t.tip.style.transform = `translate(-50%, ${(progress * t.height).toFixed(1)}px)`;
      if (progress > 0.995 && !t.splashed && t.max >= 240) {
        t.splashed = true;
        if (!reduced()) {
          t.tip.animate(
            [{ transform: t.tip.style.transform + ' scale(1)' }, { transform: t.tip.style.transform + ' scale(1.7, 0.6)', offset: 0.3 }, { transform: t.tip.style.transform + ' scale(1)' }],
            { duration: 500, easing: 'ease-out' }
          );
          const rect = t.tip.getBoundingClientRect();
          const layer = this.adopt(decorative(el('div', 'lbd-splash-layer', this)));
          splash(layer, rect.left + rect.width / 2, rect.top + rect.height / 2, 9, 34, { up: true, min: 4, max: 9 });
          this.later(() => layer.remove(), 1000);
        }
      }
    }

    /* --- 5. Secret mud splatter ---------------------------------------------------------------------------------- */

    initSecret() {
      let typed = [];
      this.on(document, 'keydown', (event) => {
        if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) return;
        const key = (event.key || '').toLowerCase();
        if (key.length !== 1) return;
        const target = event.target;
        if (target?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]') || target?.isContentEditable) return;
        const now = performance.now();
        typed.push({ key, now });
        typed = typed.filter((entry) => now - entry.now <= 1500).slice(-3);
        if (typed.map((entry) => entry.key).join('') === 'mud') {
          typed = [];
          this.triggerSecret(true);
        }
      });
    }

    triggerSecret(fromKeyboard) {
      if (store('session', SECRET_KEY) || this.secretOpen) return;
      store('session', SECRET_KEY, '1');
      this.secretOpen = true;
      play('splat');

      const still = reduced();
      if (!still) {
        const layer = this.adopt(decorative(el('div', 'lbd-splatter', this)));
        const w = window.innerWidth;
        const h = window.innerHeight;
        const count = w < 700 ? 28 : 40;
        for (let i = 0; i < count; i++) {
          const blob = el('span', 'lbd-splat', layer);
          const size = rand(18, w < 700 ? 70 : 110);
          blob.style.cssText = `left:${rand(-0.04, 1) * w - size / 2}px;top:${rand(-0.04, 0.92) * h - size / 2}px;width:${size}px;height:${size * rand(0.85, 1.1)}px;--mud:${pick(MUD)}`;
          const delay = rand(0, 260);
          const drip = rand(50, 170);
          const r = `rotate(${rand(-40, 40).toFixed(1)}deg)`;
          blob.animate(
            [
              { transform: `translateY(0) scale(0.1) ${r}`, opacity: 0 },
              { transform: `translateY(0) scale(1.18, 0.9) ${r}`, opacity: 1, offset: 0.05 },
              { transform: `translateY(0) scale(1) ${r}`, opacity: 1, offset: 0.09 },
              { transform: `translateY(0) scale(1) ${r}`, opacity: 1, offset: 0.4 },
              { transform: `translateY(${drip}px) scale(0.88, 1.28) ${r}`, opacity: 1, offset: 0.78 },
              { transform: `translateY(${drip * 1.35}px) scale(0.7, 1.45) ${r}`, opacity: 0 },
            ],
            { duration: rand(2800, 3200), delay, easing: 'cubic-bezier(.45,0,.7,1)', fill: 'both' }
          );
        }
        this.later(() => layer.remove(), 3800);
      }
      this.showSecretCard(fromKeyboard);
    }

    showSecretCard(fromKeyboard) {
      const code = (this.getAttribute('secret-code') || '').trim();
      const card = this.adopt(el('div', 'lbd-secret'));
      card.setAttribute('role', 'dialog');
      card.setAttribute('aria-label', this.getAttribute('secret-message') || 'You found the secret mud!');
      this.appendChild(card);

      const buddy = el('span', 'lbd-secret__buddy', card);
      const mini = this.mini('orange', 'lbd-secret__mini');
      buddy.appendChild(mini);
      decorative(buddy);

      const close = el('button', 'lbd-secret__close', card);
      close.type = 'button';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>';

      const text = el('p', 'lbd-secret__text', card);
      text.textContent = this.getAttribute('secret-message') || 'You found the secret mud! 🤫';

      let copyButton = null;
      if (code) {
        const row = el('div', 'lbd-secret__row', card);
        const value = el('span', 'lbd-secret__code', row);
        value.textContent = code;
        copyButton = el('button', 'lbd-secret__copy', row);
        copyButton.type = 'button';
        copyButton.textContent = 'Copy';
        copyButton.addEventListener('click', () => {
          const done = () => {
            copyButton.textContent = 'Copied!';
            this.later(() => (copyButton.textContent = 'Copy'), 1800);
          };
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(code).then(done, () => this.fallbackCopy(value, done));
          } else {
            this.fallbackCopy(value, done);
          }
        });
      }

      requestAnimationFrame(() => this.mood(mini, 'giggle', 1600));
      if (!reduced()) {
        card.animate(
          [
            { transform: 'translate(-50%, -50%) scale(0.2) rotate(-8deg)', opacity: 0 },
            { transform: 'translate(-50%, -50%) scale(1.06) rotate(-1deg)', opacity: 1, offset: 0.7 },
            { transform: 'translate(-50%, -50%) scale(1) rotate(-2deg)', opacity: 1 },
          ],
          { duration: 520, delay: 280, easing: 'cubic-bezier(.3,1.3,.5,1)', fill: 'backwards' }
        );
      }

      const onKey = (event) => {
        if (event.key === 'Escape') hide();
      };
      const hide = () => {
        if (!card.isConnected) return;
        this.secretOpen = false;
        document.removeEventListener('keydown', onKey);
        const remove = () => card.remove();
        if (reduced()) remove();
        else card.animate([{ opacity: 1 }, { opacity: 0, transform: 'translate(-50%, -46%) scale(0.9) rotate(-2deg)' }], { duration: 220, fill: 'forwards' }).finished.then(remove, remove);
      };
      close.addEventListener('click', hide);
      this.on(document, 'keydown', onKey);
      if (!code) this.later(hide, 5200);
      if (fromKeyboard) (copyButton || close).focus({ preventScroll: true });
    }

    fallbackCopy(node, done) {
      try {
        const range = document.createRange();
        range.selectNodeContents(node);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        if (document.execCommand('copy')) done();
      } catch (error) {
        /* the code stays selected so it can be copied by hand */
      }
    }

    /* --- 8. Size picker with characters -------------------------------------------------------------------------- */

    initSizes() {
      const names = (this.getAttribute('size-options') || 'Size, Age, Größe, Taille')
        .split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);
      if (!names.length) return;
      this.sizeNames = names;

      const pickers = Array.from(document.querySelectorAll('variant-picker'));
      if (!pickers.length) return;

      // The picker block (or the whole product-rerender) is replaced on variant change: watch its section
      const roots = new Set(pickers.map((picker) => picker.closest('.shopify-section') || picker.parentElement));
      let frame = 0;
      const run = () => {
        frame = 0;
        this.enhanceSizes();
      };
      roots.forEach((root) => {
        const observer = new MutationObserver(() => {
          if (!frame) frame = window.requestAnimationFrame(run);
        });
        observer.observe(root, { childList: true, subtree: true });
        this.observers.push(observer);
      });

      this.on(document, 'change', (event) => {
        const input = event.target;
        if (input?.matches?.('variant-picker input[type="radio"]')) {
          this.pickedSize = { id: input.id, at: performance.now() };
          this.bounceSize();
        }
      });

      this.enhanceSizes();
      this.removals.push(() => this.unenhanceSizes());
    }

    enhanceSizes() {
      document.querySelectorAll('variant-picker fieldset.variant-picker__option').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend');
        const name = (legend?.textContent || '').trim().replace(/:\s*$/, '').trim().toLowerCase();
        if (!this.sizeNames.includes(name)) return;
        const labels = Array.from(fieldset.querySelectorAll('.variant-picker__option-values .block-swatch'));
        const last = Math.max(1, labels.length - 1);
        labels.forEach((label, index) => {
          if (!label.classList.contains('lbd-size')) label.classList.add('lbd-size');
          const blobSize = Math.round(14 + (16 * index) / last);
          label.style.setProperty('--lbd-blob', `${labels.length === 1 ? 22 : blobSize}px`);
          label.style.setProperty('--lbd-c', SIZE_COLORS[index % SIZE_COLORS.length]);
          if (!label.querySelector(':scope > .lbd-size__blob')) {
            const blob = decorative(el('span', 'lbd-size__blob'));
            el('span', 'lbd-size__eyes', blob);
            label.prepend(blob);
          }
        });
      });
      this.bounceSize();
    }

    bounceSize() {
      const picked = this.pickedSize;
      if (!picked || performance.now() - picked.at > 1500 || reduced()) return;
      const input = document.getElementById(picked.id);
      // A re-render that replaces the label mid-bounce gets the new label bounced; later re-renders are left alone
      if (picked.blob && (picked.blob.isConnected || performance.now() - picked.at > 450)) return;
      const blob = input?.nextElementSibling?.querySelector?.(':scope > .lbd-size__blob');
      if (!blob) return;
      picked.blob = blob;
      blob.animate(
        [
          { transform: 'translateY(0) scale(1, 1)' },
          { transform: 'translateY(1px) scale(1.3, 0.7)', offset: 0.18 },
          { transform: 'translateY(-7px) scale(0.86, 1.16)', offset: 0.45 },
          { transform: 'translateY(0) scale(1.12, 0.9)', offset: 0.72 },
          { transform: 'translateY(0) scale(1, 1)' },
        ],
        { duration: 560, easing: 'ease-out' }
      );
    }

    unenhanceSizes() {
      document.querySelectorAll('.lbd-size').forEach((label) => {
        label.classList.remove('lbd-size');
        label.style.removeProperty('--lbd-blob');
        label.style.removeProperty('--lbd-c');
        label.querySelector(':scope > .lbd-size__blob')?.remove();
      });
    }
  }

  window.customElements.define('luvmud-buddies', LuvmudBuddies);
})();
